import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, Check } from "@phosphor-icons/react";

const hrs = (n) =>
    (Number(n) || 0).toLocaleString("es-ES", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
    });

export default function AssignmentsMatrix() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        http.get("/projects/assignments-matrix")
            .then((r) => setData(r.data))
            .finally(() => setBusy(false));
    }, []);

    const projects = useMemo(() => {
        if (!data) return [];
        const q = search.trim().toLowerCase();
        return data.projects.filter(
            (p) =>
                !q ||
                (p.name || "").toLowerCase().includes(q) ||
                (p.project_code || "").toLowerCase().includes(q),
        );
    }, [data, search]);

    if (busy) {
        return <div className="p-10 text-slate-400 text-sm">Cargando cuadro…</div>;
    }
    if (!data) return null;

    const users = data.users;
    const cell = (pid, uid) => data.cells[`${pid}|${uid}`];

    return (
        <div className="space-y-4" data-testid="matrix-view">
            <div className="flex items-center gap-3">
                <div className="relative w-72">
                    <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-slate-400" />
                    <Input
                        placeholder="Buscar proyecto..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="rounded-sm border-slate-300 pl-7 h-8"
                        data-testid="matrix-search"
                    />
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500 ml-auto">
                    <span className="flex items-center gap-1.5">
                        <Check size={13} className="text-emerald-600" /> Asignado
                    </span>
                    <span className="font-mono text-slate-400">
                        {projects.length} proyectos · {users.length} personas
                    </span>
                </div>
            </div>

            <div className="border border-slate-200 bg-white overflow-auto max-h-[70vh]">
                <table className="text-sm border-collapse">
                    <thead>
                        <tr>
                            <th className="sticky left-0 top-0 z-20 bg-slate-100 border-b border-r border-slate-200 px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 min-w-[240px]">
                                Proyecto
                            </th>
                            {users.map((u) => (
                                <th
                                    key={u.user_id}
                                    className="sticky top-0 z-10 bg-slate-100 border-b border-slate-200 px-1.5 py-2 text-center align-bottom"
                                    title={u.name}
                                >
                                    <div
                                        className="text-[10px] text-slate-600 font-medium whitespace-nowrap"
                                        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                                    >
                                        {u.name}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {projects.map((p) => (
                            <tr key={p.project_id} className="hover:bg-slate-50">
                                <td className="sticky left-0 z-10 bg-white hover:bg-slate-50 border-b border-r border-slate-100 px-3 py-2">
                                    <div className="text-slate-800 font-medium truncate max-w-[220px]">
                                        {p.name}
                                    </div>
                                    <div className="text-[10px] text-slate-400 font-mono">
                                        {p.project_code || "—"}
                                    </div>
                                </td>
                                {users.map((u) => {
                                    const c = cell(p.project_id, u.user_id);
                                    const assigned = c?.assigned;
                                    const h = c?.hours || 0;
                                    return (
                                        <td
                                            key={u.user_id}
                                            className={`border-b border-slate-100 text-center px-1.5 py-2 whitespace-nowrap ${
                                                assigned ? "bg-emerald-50/60" : ""
                                            }`}
                                            data-testid={`cell-${p.project_id}-${u.user_id}`}
                                        >
                                            {assigned && (
                                                <Check
                                                    size={12}
                                                    className="text-emerald-600 inline"
                                                    weight="bold"
                                                />
                                            )}
                                            {h > 0 && (
                                                <span
                                                    className={`text-[11px] font-mono ml-0.5 ${
                                                        assigned
                                                            ? "text-slate-700"
                                                            : "text-slate-400 italic"
                                                    }`}
                                                >
                                                    {hrs(h)}
                                                </span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="text-[11px] text-slate-400">
                La marca indica asignación al proyecto; el número son las horas imputadas. Las
                horas en gris cursiva corresponden a imputaciones sin asignación activa.
            </p>
        </div>
    );
}
